import React, { useState } from 'react';
import { PickerMenuOption } from '../../shared/picker/PickerMenuOption';
import { PickerMenuDivider, PickerMenuShell } from '../../shared/picker/PickerMenuShell';
import { ExtremeReasonForm } from './ExtremeReasonForm.js';
import type { PriorityLevel } from './priority.js';
import { PRIORITY_LEVELS } from './priority.js';
import { PriorityGlyph } from './PriorityGlyphs.js';
import { PRIORITY_LABEL_TONES } from './priorityTones.js';

interface PriorityMenuProps {
    current: PriorityLevel;
    onSelect: (priority: PriorityLevel, reason?: string) => void;
    heading?: string;
    // Off: Extreme is one click, with no reason posted to the case timeline.
    requireExtremeReason?: boolean;
}

type Stage = 'pick' | 'extreme-reason';

const OPTIONS: PriorityLevel[] = ['normal', 'high', 'extreme'];

export function PriorityMenu({
    current,
    onSelect,
    heading = 'Priority',
    requireExtremeReason = true
}: PriorityMenuProps) {
    const [stage, setStage] = useState<Stage>('pick');

    const handlePick = (level: PriorityLevel) => {
        if (requireExtremeReason && level === 'extreme' && current !== 'extreme') {
            setStage('extreme-reason');
            return;
        }
        onSelect(level);
    };

    if (stage === 'extreme-reason') {
        return (
            <PickerMenuShell key={stage}>
                <ExtremeReasonForm
                    onSubmit={(reason) => onSelect('extreme', reason)}
                    onBack={() => setStage('pick')}
                />
            </PickerMenuShell>
        );
    }

    return (
        <PickerMenuShell key={stage} heading={heading}>
            {OPTIONS.map((level) => (
                <React.Fragment key={level}>
                    {level === 'extreme' && <PickerMenuDivider />}
                    <PickerMenuOption
                        glyph={<PriorityGlyph priority={level} />}
                        label={PRIORITY_LEVELS[level].label}
                        description={PRIORITY_LEVELS[level].description}
                        labelClassName={PRIORITY_LABEL_TONES[level]}
                        selected={level === current}
                        onPick={() => handlePick(level)}
                    />
                </React.Fragment>
            ))}
        </PickerMenuShell>
    );
}
